import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Checkbox, Form, Icon, Popup } from "semantic-ui-react";

import { Can } from "components";
import { Element } from "components/ReactScroll/index";
import ENV from "config/Env";
import { createFormHelper } from "lib/Form";
import { ACL_ACTIVITIES, ACL_RESOURCES, HEADING_POSITIONS } from "models";
import "modules/CommunicationSettings/components/General/General.scss";
import "modules/CommunicationSettings/CustomerCom.scss";

import type { CommunicationSetting } from "models";

type GeneralProps = {
  tabId: string;
};

const FormHelper = createFormHelper<CommunicationSetting>();

export const General = ({ tabId }: GeneralProps) => {
  const { t } = useTranslation();

  const layoutOptions = useMemo(
    () => [
      { key: 1, value: 1, text: t("v8_default_layout", "Default layout") },
      { key: 2, value: 2, text: t("v8_default_layout_menu_left", "Default layout with menu on the left") }
    ],
    []
  );

  const { setValue, watch } = FormHelper.useFormContext();
  const headingPosition = watch("heading_position");

  return (
    <Element name={tabId} className="customer-com-wrapper">
      <h3>{t("v8_general", "General")}</h3>
      <div>
        <Form.Group className="mt-18">
          <Form.Field width={8}>
            <label>{t("v8_email", "E-mail")}</label>
            <FormHelper.Field.Input name="email" inputProps={{ fluid: true, placeholder: t("v8_email", "E-mail") }} />
          </Form.Field>

          <Form.Field width={8}>
            <label>{t("v8_phone", "Phone")}</label>
            <FormHelper.Field.Input name="phone_number" inputProps={{ fluid: true, placeholder: t("v8_phone", "Phone"), type: "Phone" }} />
          </Form.Field>
        </Form.Group>

        <Form.Group className="mt-14">
          <Form.Field width={8}>
            <label>{t("v8_website", "Website")}</label>
            <FormHelper.Field.Input name="website" inputProps={{ fluid: true, placeholder: t("v8_website", "Website") }} />
          </Form.Field>

          <Form.Field width={8}>
            <label>{t("v8_layout", "Layout")}</label>
            <FormHelper.Field.Dropdown name="layout" options={layoutOptions} dropdownProps={{ placeholder: t("v8_select_layout", "Select Layout"), selection: true }} />
          </Form.Field>
        </Form.Group>

        <Form.Group className="mt-14">
          <Form.Field width={8}>
            <label>{t("v8_external_CSS", "External CSS")}</label>
            <FormHelper.Field.Input name="external_css" inputProps={{ fluid: true, placeholder: t("v8_external_CSS", "External CSS") }} />
          </Form.Field>

          <Form.Field width={8}>
            <label>{t("v8_external_JS", "External JS")}</label>
            <FormHelper.Field.Input name="external_js" inputProps={{ fluid: true, placeholder: t("v8_external_JS", "External JS") }} />
          </Form.Field>
        </Form.Group>

        <Form.Group className="mt-14">
          <Form.Field width={8} className="pr-12">
            <label>{t("v8_google_analytics_ID", "Google Analytics ID")}</label>
            <FormHelper.Field.Input name="google_analytics_id" inputProps={{ fluid: true, placeholder: t("v8_google_analytics_ID", "Google Analytics ID") }} />
          </Form.Field>
        </Form.Group>

        <Form.Group className="mt-16">
          <Form.Field width={8} className="row items-center">
            <span>{t("v8_pick_a_colour_for_customer_communication", "Pick a colour for customer communication")}</span>
            <div className="color-picker">
              <FormHelper.Field.ColorPicker name="color" />
            </div>
          </Form.Field>
        </Form.Group>

        <Form.Group className="mt-18">
          <Form.Field width={8}>
            <p className="title">{t("v8_automated_communications", "Automated communications")}</p>
            <span className="items-center">
              <FormHelper.Field.Checkbox
                name="is_sms_for_automated_customer_communication_enabled"
                label={t("v8_enable_sending_sms_for_automated_communication", "Enable sending SMS for automated communication")}
              />
            </span>
            <span className="items-center">
              <FormHelper.Field.Checkbox
                name="is_bird_whats_app_for_automated_customer_communication_enabled"
                label={t("v8_enable_sending_whatsapp_for_automated_communication", "Enable sending WhatsApp for automated communication")}
              />
            </span>
          </Form.Field>

          <Form.Field width={8} className="confirmation-emails-field">
            <p className="title">{t("v8_confirmation_emails", "Confirmation Emails")}</p>
            <span className="items-center">
              <FormHelper.Field.Checkbox name="send_confirmation_emails" label={t("v8_enable_sending_confirmation_emails", "Enable sending confirmation emails")} />
            </span>
          </Form.Field>
        </Form.Group>

        <Form.Group className="mt-18">
          <Form.Field width={8}>
            <p className="title">{t("v8_heading_position", "Heading position")}</p>
            <span className="items-center">
              <Checkbox checked={headingPosition === HEADING_POSITIONS.HeadingTop} onClick={() => setValue("heading_position", 1)} />
              <span className="checkbox-label">{t("v8_logo_and_info_in_header", "Logo and info in Header")}</span>
            </span>
            <span className="items-center">
              <Checkbox checked={headingPosition === HEADING_POSITIONS.HeadingBottom} onClick={() => setValue("heading_position", 2)} />
              <span className="checkbox-label">{t("v8_logo_and_info_in_footer", "Logo and info in Footer")}</span>
            </span>
          </Form.Field>

          <Form.Field width={8}>
            <p className="title">{t("v8_price", "Price")}</p>
            <span className="items-center">
              <FormHelper.Field.Checkbox
                name="hide_lease_intervention_price"
                label={t("v8_hide_price_of_interventions_for_lease_appointments", "Hide price of interventions for Lease appointments")}
              />
            </span>
            <span className="items-center">
              <FormHelper.Field.Checkbox
                name="hide_internal_intervention_price"
                label={t("v8_hide_internal_intervention_price", "Hide price of internal interventions")}
              />
            </span>
            <span className="items-center">
              <FormHelper.Field.Checkbox
                name="hide_warranty_intervention_price"
                label={t("v8_hide_warranty_intervention_price", "Hide price of warranty interventions")}
              />
            </span>
            <span className="items-center">
              <FormHelper.Field.Checkbox name="hide_recall_intervention_price" label={t("v8_hide_recall_intervention_price", "Hide price of recall interventions")} />
            </span>
            <span className="items-center">
              <FormHelper.Field.Checkbox
                name="hide_prices_in_desk_communication"
                label={t("v8_hide_prices_in_desk_communication", "Hide price for the desk check-in/out")}
              />
            </span>
          </Form.Field>
        </Form.Group>

        <Form.Group className="mt-18">
          <Form.Field width={8}>
            <p className="title">{t("v8_scheduled_services", "Scheduled services")}</p>
            <span className="items-center">
              <FormHelper.Field.Checkbox name="display_intervention_elements" label={t("v8_display_intervention_elements", "Display Intervention Elements")} />
            </span>
          </Form.Field>

          <Form.Field width={8}>
            <p className="title">{t("v8_warranty_items", "Warranty items")}</p>
            <span className="items-center">
              <FormHelper.Field.Checkbox name="cannot_decline_warranty_types" label={t("v8_customer_cannot_decline_warranty", "Customer cannot decline warranty")} />
            </span>
          </Form.Field>
        </Form.Group>

        <Form.Group>
          <Form.Field width={16}>
            <p className="title">{t("v8_popup", "Popup")}</p>
            <span className="items-center">
              <Form.Field>
                <span className="items-center">
                  <FormHelper.Field.Checkbox name="enable_closing_pop_up_on_cc" label={t("v8_allow_close", "Allow close")} />
                </span>
              </Form.Field>
            </span>
          </Form.Field>
        </Form.Group>

        <Form.Group className="mt-25 file">
          <Can I={ACL_ACTIVITIES.Update} the={ACL_RESOURCES.CommunicationSettingsLogo}>
            <Form.Field width={8}>
              <label className="small">
                <span>{t("v8_upload_logo", "Upload Logo")} (.jpg, .jpeg, .png)</span>
                <Popup content={t("v8_min_size_of_logo", "Min size of Logo 100x100 px")} position="right center" trigger={<Icon className="circle info" />} />
              </label>
              <FormHelper.Field.UploadImage path="/communication/logo/upload" baseUrl={ENV.dealerBaseURL} name="logo" fileFieldName="file" />
            </Form.Field>
          </Can>

          <Can I={ACL_ACTIVITIES.Update} the={ACL_RESOURCES.CommunicationSettingsWelcomePicture}>
            <Form.Field width={8}>
              <label className="small">
                <span>{t("v8_upload_welcome_image", "Upload welcome image")} (.jpg, .jpeg, .png) </span>
                <Popup content={t("v8_min_size_of_welcome_pic", "Min size of welcome picture")} position="right center" trigger={<Icon className="circle info" />} />
              </label>
              <FormHelper.Field.UploadImage path="/communication/welcome_pic/upload" baseUrl={ENV.dealerBaseURL} name="welcome_picture" fileFieldName="file" />
            </Form.Field>
          </Can>
        </Form.Group>
      </div>
    </Element>
  );
};
